import _ from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import InsetContainer from 'components/common/containers/inset_container';
import { PortalledPopover, POPOVER_PROPS } from 'components/common/lib/_popover';

import useWindowSize from 'components/hooks/use_window_size';

// Contains close() callback so items can close menu after clicking.
const PopoverMenuContext = createContext({
  onClose: () => {},
});

/**
 * @visibleName Popover Menu
 */

export default function PopoverMenu(props) {
  const { 'data-aid': aid, boundByWindow, className, isOpen, onClickOutside, onClose, target, targetRef } = props;

  useMenuKeyboardNavigation(isOpen, onClose);

  const popoverProps = {
    'data-aid': aid,
    autoPosition: true,
    className,
    isVisible: isOpen,
    onClickOutside: onClickOutside !== null ? onClose : undefined,
    position: props.position,
    role: 'menu',
    target,
    targetElement: targetRef,
    ..._.pick(props, POPOVER_PROPS),
  };

  const { windowHeight, windowWidth } = useWindowSize();
  if (boundByWindow) {
    popoverProps.bounds = {
      bottom: windowHeight - 68,
      left: 8,
      right: windowWidth - 8,
      top: 68,
    };
  }

  const contextValue = useMemo(
    () => ({
      onClose,
    }),
    [onClose]
  );

  return (
    <PopoverMenuContext.Provider value={contextValue}>
      <StyledPopoverMenu {...popoverProps} />
    </PopoverMenuContext.Provider>
  );
}

export function usePopoverMenu(positionRef) {
  const [targetRef, setTargetRef] = useState(positionRef || null);
  const [isOpen, setIsOpen] = useState(false);
  const onClose = useCallback(() => setIsOpen(false), []);
  const onToggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const onOpen = useCallback(() => setIsOpen(true), []);

  return { targetRef, setTargetRef, isOpen, onClose, onToggle, onOpen };
}

function useMenuKeyboardNavigation(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return;

    const getMenuItems = menuEl => Array.from(menuEl.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])'));

    const focusTimerId = setTimeout(() => {
      const active = document.activeElement;
      const isUserTyping = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
      if (isUserTyping) return;

      const menus = document.querySelectorAll('[role="menu"]');
      const menu = menus[menus.length - 1];
      if (menu) {
        const items = getMenuItems(menu);
        if (items.length) items[0].focus();
      }
    }, 0);

    function onKeyDown(e) {
      const menu = document.activeElement && document.activeElement.closest('[role="menu"]');
      if (!menu) return;

      const items = getMenuItems(menu);
      if (!items.length) return;

      const currentIndex = items.indexOf(document.activeElement);

      switch (e.key) {
        case 'ArrowDown':
        case 'Down':
          e.stopPropagation();
          e.preventDefault();
          if (currentIndex < items.length - 1) {
            items[currentIndex + 1].focus();
          } else {
            items[0].focus();
          }
          return;
        case 'ArrowUp':
        case 'Up':
          e.stopPropagation();
          e.preventDefault();
          if (currentIndex > 0) {
            items[currentIndex - 1].focus();
          } else {
            items[items.length - 1].focus();
          }
          return;
        case 'Home':
          e.stopPropagation();
          e.preventDefault();
          items[0].focus();
          return;
        case 'End':
          e.stopPropagation();
          e.preventDefault();
          items[items.length - 1].focus();
          return;
        case 'Escape':
        case 'Esc':
          e.stopPropagation();
          onClose();
          return;
        case 'Tab':
          e.stopPropagation();
          e.preventDefault();
          return;
        default:
          return;
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => {
      clearTimeout(focusTimerId);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);
}

PopoverMenu.defaultProps = {
  boundByWindow: true,
  isOpen: false,
  position: 'top',
};

const StyledPopoverMenu = styled(PortalledPopover)`
  background-color: ${p => p.theme.colors.white};
  border-radius: 6px;
  box-shadow: ${p => p.theme.boxShadow.medium};
  color: ${p => p.theme.colors.black};
  overflow: hidden;
`;

const StyledMenuItem = styled(InsetContainer)`
  cursor: pointer;
  min-width: 100px;
  padding: 8px 16px;
  word-break: break-word;
  &:hover,
  &:focus {
    background: ${p => p.theme.colors.green100};
    outline: none;
  }
  ${p => p.isHovered && hoveredMenuItem};
  ${p => p.isDisabled && disabledMenuItem};
  ${p => p.isFocused && !p.isDisabled && !p.isMultiSelect && focusedMenuItem};
`;

const hoveredMenuItem = css`
  background: ${p => p.theme.colors.green100};
`;

const disabledMenuItem = css`
  color: ${p => p.theme.colors.gray600};
  cursor: default;
  &:hover,
  &:focus {
    background: ${p => p.theme.colors.white};
  }
`;

const focusedMenuItem = css`
  background-color: ${p => p.theme.colors.green400};
  color: ${p => p.theme.colors.white};
  cursor: default;
  &:hover {
    background-color: ${p => p.theme.colors.green400};
    color: ${p => p.theme.colors.white};
  }
`;

StyledMenuItem.defaultProps = { inset: 'small' };

export { StyledMenuItem };

export function PopoverMenuItem({
  className,
  component,
  children,
  'data-aid': aid,
  disableFocusOnHover,
  isDisabled,
  isFocused,
  isHovered,
  isMultiSelect,
  onClick,
  onClose,
  onMouseDown,
  onMouseOver,
}) {
  const popoverContext = useContext(PopoverMenuContext);
  onClose = onClose ? onClose : popoverContext.onClose;
  const decoratedOnClick = useCallback(
    event => {
      if (!isDisabled) {
        onClick && onClick(event);

        if (!isMultiSelect) {
          onClose && onClose();
        }
      }
    },
    [isDisabled, onClose, onClick, isMultiSelect]
  );

  const handleKeyDown = useCallback(
    event => {
      if (isDisabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        decoratedOnClick(event);
      }
    },
    [isDisabled, decoratedOnClick]
  );

  const handleMouseEnter = useCallback(
    event => {
      if (!isDisabled && !disableFocusOnHover) {
        event.currentTarget.focus();
      }
    },
    [isDisabled, disableFocusOnHover]
  );

  const Component = component || StyledMenuItem;
  return (
    <Component
      aria-disabled={isDisabled || undefined}
      className={className}
      data-aid={aid}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isHovered={isHovered}
      isMultiSelect={isMultiSelect}
      onClick={decoratedOnClick}
      onKeyDown={handleKeyDown}
      onMouseDown={!isDisabled ? onMouseDown : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseOver={onMouseOver}
      role="menuitem"
      tabIndex="-1"
    >
      {children}
    </Component>
  );
}

export { StyledPopoverMenu };

PopoverMenuItem.propTypes = {
  className: PropTypes.string,
  children: PropTypes.any,
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  disableFocusOnHover: PropTypes.bool,
  isDisabled: PropTypes.bool,
  'data-aid': PropTypes.string,
  isFocused: PropTypes.bool,
  isHovered: PropTypes.bool,
  /** Boolean to determine if dropdown menu should allow multiple selections */
  isMultiSelect: PropTypes.bool,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseOver: PropTypes.func,
};

PopoverMenu.propTypes = PortalledPopover.propTypes;
